// @ts-nocheck
import classNames from 'classnames';
import { uniq } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BoxV2, ButtonUI } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { formatAdsFormat, getUniquesAdsFormatMultiCampaign } from '@/helpers/reporting.helpers';
import {
  reportAdvancedFilterApplySettings,
  reportingAdvancedFilterSetCampaignIds
} from '@/store/actions/reportingAdvancedFilter.actions';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ActionButton } from '../components/ActionButton';
import { Detail } from '../components/Detail';
import { Logo } from '../components/Logo';
import { BoxStyled } from '../header.styles';
import { EditSelectionModal } from './components/EditSelectionModal';
import { MultiDetails } from './components/MultiDetails';
import { MultiBoxStyled } from './multi.styles';
import './styles.scss';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

const getId = (value: string) => `reporting.header.multi.${value}`;

type Props = {
  currencyInfo: BaseCurrencyInterface;
  data: CampaignItem[];
  onSelectedCampaign: (selectedCampaigns: CampaignItem[]) => void;
};

function Multi({ currencyInfo, data: allData, onSelectedCampaign }: Props) {
  const { formatMessage } = useIntl();

  const cids = useSelector(getCampaigns);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const dispatch = useDispatch();

  const data = useMemo(() => uniq(allData.filter(c => cids.includes(c.id))), [allData, cids]);

  const [{ dataSelectedCampaigns, modalVisible, status }, setState] = useState({
    dataSelectedCampaigns: data,
    modalVisible: false,
    status: 'not-expanded'
  });

  const campaignsSelectorPermission = checkUserResourcePermissions('reportingHeaderCampaignsSelector', user, userRoles);
  const logoPermissions = checkUserResourcePermissions('reportingLogo', user, userRoles);
  const showLogo = status === 'expanded' && logoPermissions;
  const budgetPermissions = checkUserResourcePermissions('reportingBudget', user, userRoles);

  const updateStatus = () => {
    setState(prevState => ({
      ...prevState,
      status: status === 'not-expanded' ? 'expanded' : 'not-expanded'
    }));
  };

  const advertisers = useMemo(() => {
    const newAdvertisers = dataSelectedCampaigns.map(d => {
      if (d.mediaAdvertiser === null) {
        return formatMessage({
          defaultMessage: 'No Advertiser',
          id: getId('details.noAdvertiser')
        });
      }
      return d.mediaAdvertiser.name;
    });
    return uniq(newAdvertisers).sort();
  }, [dataSelectedCampaigns]);

  const pricingTypes = useMemo(() => {
    const pricingType = dataSelectedCampaigns.map(d => {
      return d.bidModels[0].bid.pricingModel.replace(/^(f-|d-)/, '').toUpperCase();
    });
    return uniq(pricingType);
  }, [dataSelectedCampaigns]);

  const onOpenEditSelectionModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      modalVisible: true
    }));
  }, []);

  const onCloseEditSelectionModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      modalVisible: false
    }));
  }, []);

  const padValue = (value: number) => (value < 10 ? `0${value}` : value.toString());

  const onSaveCampaign = useCallback((items: CampaignItem[]) => {
    setState(prevState => ({
      ...prevState,
      dataSelectedCampaigns: items
    }));
    dispatch(reportingAdvancedFilterSetCampaignIds(items.map(i => i.id)));
    dispatch(reportAdvancedFilterApplySettings('general'));
    onSelectedCampaign(items);
  }, []);

  const totalBudget = dataSelectedCampaigns
    .map(d => d.budget)
    .reduce((acc, cur) => {
      return acc + cur;
    });
  const formattedBudget = normalizeWithUnit(totalBudget, currencyInfo, 2);
  const typeOfAdsFormat = getUniquesAdsFormatMultiCampaign(dataSelectedCampaigns);
  const formattedAdsFormat = formatAdsFormat(typeOfAdsFormat);

  return (
    <>
      <section className="multiHeader">
        {showLogo && <Logo data={data} />}
        <div className="multiHeader__boxContent">
          <div
            className={classNames('multiHeader__boxInfoContainer', {
              'multiHeader__boxInfoContainer--expanded': status === 'expanded'
            })}
          >
            <div className="multiHeader__multiDetailsContainer">
              <MultiBoxStyled className="multiDetails" id="multiDetails-totalCampaigns-box">
                <BoxV2.Body className="multiDetails__body" padding="8px">
                  <MultiDetails
                    icon="fal fa-list-alt"
                    id="multiDetails__totalCampaigns"
                    label={formatMessage({
                      defaultMessage: 'Campaign Selected',
                      id: getId('details.campaignSelected')
                    })}
                    multiDetailId="totalCampaigns"
                    totalValue={padValue(allData.length)}
                    value={padValue(dataSelectedCampaigns.length)}
                  />
                </BoxV2.Body>
              </MultiBoxStyled>
              <MultiBoxStyled className="multiDetails" id="multiDetails-advertisers-box">
                <BoxV2.Body className="multiDetails__body" padding="8px">
                  <MultiDetails
                    icon="far fa-tags"
                    id="multiDetails__advertisers"
                    label={formatMessage({
                      defaultMessage: 'Advertisers',
                      id: getId('details.advertisers')
                    })}
                    multiDetailId="advertisers"
                    value={padValue(advertisers.length)}
                  />
                </BoxV2.Body>
              </MultiBoxStyled>
              <MultiBoxStyled className="multiDetails" id="multiDetails-adFormat-box">
                <BoxV2.Body className="multiDetails__body" padding="8px">
                  <MultiDetails
                    icon="far fa-box"
                    id="multiDetails__adFormat"
                    label={formatMessage({
                      defaultMessage: 'Ad type',
                      id: getId('details.adType')
                    })}
                    multiDetailId="adFormat"
                    value={padValue(formattedAdsFormat.length)}
                  />
                </BoxV2.Body>
              </MultiBoxStyled>
              <MultiBoxStyled className="multiDetails" id="multiDetails-pricing-box">
                <BoxV2.Body className="multiDetails__body" padding="8px">
                  <MultiDetails
                    icon="far fa-coin"
                    id="multiDetails__pricing"
                    label={formatMessage({
                      defaultMessage: 'Pricing types',
                      id: getId('details.pricingTypes')
                    })}
                    multiDetailId="pricing"
                    value={padValue(pricingTypes.length)}
                  />
                </BoxV2.Body>
              </MultiBoxStyled>
              {budgetPermissions && (
                <MultiBoxStyled className="multiDetails" id="multiDetails-budget-box">
                  <BoxV2.Body className="multiDetails__body" padding="8px">
                    <MultiDetails
                      icon="far fa-wallet"
                      id="multiDetails__budget"
                      label={formatMessage({
                        defaultMessage: 'Total Budget',
                        id: getId('details.totalBudget')
                      })}
                      multiDetailId="budget"
                      value={formattedBudget}
                    />
                  </BoxV2.Body>
                </MultiBoxStyled>
              )}
            </div>
            <div className="multiHeader__editSection">
              {campaignsSelectorPermission && (
                <ButtonUI
                  className="multiHeader__button"
                  icon="far fa-edit"
                  iconPosition="left"
                  id="edit_selection_modal_button"
                  onClick={onOpenEditSelectionModal}
                  shape="link"
                  size="small"
                  text="Edit selection"
                  type="secondary"
                  visible
                />
              )}
              {status === 'not-expanded' && <ActionButton icon="far fa-plus" onClick={updateStatus} />}
            </div>
          </div>
          {status === 'expanded' && (
            <div className="multiHeader__descriptionDetails">
              <div className="multiHeader__descriptionBoxDetails">
                <BoxStyled borderColor="boxBorderColor01" header="multi" id="multiHeader-box">
                  <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                    <Detail
                      icon="far fa-tags"
                      label={formatMessage({
                        defaultMessage: 'Advertiser',
                        id: getId('details.advertiser')
                      })}
                      showTitle
                      value={advertisers.join(', ')}
                    />
                  </BoxV2.Body>
                </BoxStyled>
                <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box">
                  <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                    <Detail
                      icon="far fa-box"
                      label={formatMessage({
                        defaultMessage: 'Ad type',
                        id: getId('details.adType')
                      })}
                      value={formattedAdsFormat.join(', ')}
                    />
                  </BoxV2.Body>
                </BoxStyled>
                <BoxStyled borderColor="boxBorderColor01" header="multi" id="multiHeader-box">
                  <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                    <Detail
                      icon="far fa-coin"
                      label={formatMessage({
                        defaultMessage: 'Pricing type',
                        id: getId('details.pricingType')
                      })}
                      value={pricingTypes.join(', ')}
                    />
                  </BoxV2.Body>
                </BoxStyled>
              </div>
              <ActionButton icon="far fa-minus" onClick={updateStatus} />
            </div>
          )}
        </div>
      </section>
      <EditSelectionModal
        data={data}
        isModalOpened={modalVisible}
        onClose={onCloseEditSelectionModal}
        onSaveCampaignsSelected={onSaveCampaign}
      />
    </>
  );
}

export default React.memo(Multi);
